import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Nav } from "../components/landing/Nav";
import { Footer } from "../components/landing/Footer";
import { SITE } from "../config/site";
import { useSeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENQUIRY_TYPES = [
  "General",
  "Artist",
  "Designer",
  "Press & media",
  "Partnership",
  "Other",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactPage() {
  useSeo("/contact");

  const [form, setForm] = useState({
    name: "",
    email: "",
    enquiry_type: "General",
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const message = form.message.trim();
    if (!name) return toast.error("Please enter your name.");
    if (!EMAIL_RE.test(form.email.trim())) return toast.error("Please enter a valid email address.");
    if (!message) return toast.error("Please enter a message.");
    if (!form.consent) return toast.error("Please tick the consent box to send your message.");

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name,
        email: form.email.trim(),
        enquiry_type: form.enquiry_type,
        message,
        consent: true,
      });
      toast.success("Thanks — we'll reply within 48 hours.");
      setDone(true);
      setForm({ name: "", email: "", enquiry_type: "General", message: "", consent: false });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error("Too many messages. Please try again shortly.");
      else toast.error("Something went wrong. Please try again or email hello@vynlworld.com.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    backgroundColor: "#0E0A16",
    border: "1px solid #2A2233",
    color: "#F4EEF7",
  };
  const labelClass = "text-[11px] uppercase tracking-[0.22em] mb-3 block";
  const labelStyle = { color: "#9B8FAC" };

  return (
    <div className="grain min-h-screen" style={{ backgroundColor: "#0E0A16" }} data-testid="contact-page">
      <Nav />

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 pt-40 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20"
        >
          {/* Left column: heading + copy + soft pointer to founding form */}
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.35em] mb-6" style={{ color: "#C724B1" }}>
              Contact
            </div>
            <h1
              className="font-inter font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.1] mb-6"
              style={{ color: "#F4EEF7", letterSpacing: "-0.01em" }}
            >
              Get in touch.
            </h1>
            <p className="text-lg leading-relaxed mb-10 max-w-[46ch]" style={{ color: "#9B8FAC" }}>
              Questions, press, partnerships — or just want to talk merch. We reply within 48 hours.
            </p>

            <div className="mt-6 mb-10 space-y-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] mb-2" style={labelStyle}>Direct email</div>
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="contact-direct-email"
                  className="text-lg underline transition-colors duration-300 hover:text-[#C724B1]"
                  style={{ color: "#F4EEF7" }}
                >
                  {SITE.email}
                </a>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] mb-2" style={labelStyle}>Instagram</div>
                <a
                  href={SITE.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram"
                  className="text-lg underline transition-colors duration-300 hover:text-[#C724B1]"
                  style={{ color: "#F4EEF7" }}
                >
                  {SITE.instagramHandle}
                </a>
              </div>
            </div>

            <div className="pt-6" style={{ borderTop: "1px solid #2A2233" }}>
              <p className="text-sm leading-relaxed" style={{ color: "#9B8FAC" }}>
                Want to sell your merch on VYNLWORLD?{" "}
                <a
                  href="/#founding"
                  data-testid="contact-founding-pointer"
                  className="underline transition-colors duration-300 hover:text-[#C724B1]"
                  style={{ color: "#F4EEF7" }}
                >
                  The founding-artist form is quicker &rarr;
                </a>
              </p>
            </div>
          </div>

          {/* Right column: enquiry form */}
          <div className="lg:col-span-7">
            {done ? (
              <div
                data-testid="contact-success"
                className="p-8"
                style={{ backgroundColor: "#1A1424", border: "1px solid #2A2233" }}
              >
                <div className="text-[11px] uppercase tracking-[0.22em] mb-4" style={{ color: "#C724B1" }}>Message received</div>
                <h2 className="font-inter font-light text-2xl md:text-3xl mb-4" style={{ color: "#F4EEF7" }}>
                  Thanks — we'll reply within 48 hours.
                </h2>
                <p className="text-base leading-relaxed mb-6" style={{ color: "#9B8FAC" }}>
                  A confirmation is on its way to your inbox. If you need to add anything, reply to that email or write to{" "}
                  <a href={`mailto:${SITE.email}`} className="underline hover:text-[#C724B1]" style={{ color: "#F4EEF7" }}>{SITE.email}</a>.
                </p>
                <button
                  data-testid="contact-send-another-btn"
                  onClick={() => setDone(false)}
                  className="btn-sharp text-xs uppercase tracking-[0.22em] px-6 py-3 font-medium"
                  style={{ border: "1px solid #F4EEF7", color: "#F4EEF7" }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                noValidate
                onSubmit={submit}
                className="p-8 space-y-6"
                style={{ backgroundColor: "#1A1424", border: "1px solid #2A2233" }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-name" className={labelClass} style={labelStyle}>Name</label>
                    <input
                      id="contact-name"
                      data-testid="contact-name"
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      required
                      className="w-full px-4 py-3 text-base outline-none transition-colors duration-300 focus:border-[#C724B1]"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className={labelClass} style={labelStyle}>Email</label>
                    <input
                      id="contact-email"
                      data-testid="contact-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      required
                      className="w-full px-4 py-3 text-base outline-none transition-colors duration-300 focus:border-[#C724B1]"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-type" className={labelClass} style={labelStyle}>I'm getting in touch about</label>
                  <select
                    id="contact-type"
                    data-testid="contact-enquiry-type"
                    value={form.enquiry_type}
                    onChange={(e) => update("enquiry_type", e.target.value)}
                    className="w-full px-4 py-3 text-base outline-none transition-colors duration-300 focus:border-[#C724B1] appearance-none cursor-pointer"
                    style={inputStyle}
                  >
                    {ENQUIRY_TYPES.map((t) => (
                      <option key={t} value={t} style={{ backgroundColor: "#0E0A16", color: "#F4EEF7" }}>{t}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className={labelClass} style={labelStyle}>Message</label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message"
                    rows={7}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    required
                    className="w-full px-4 py-3 text-base outline-none transition-colors duration-300 focus:border-[#C724B1] resize-y"
                    style={inputStyle}
                  />
                </div>

                <label
                  htmlFor="contact-consent"
                  className="flex items-start gap-3 cursor-pointer"
                  style={{ color: "#9B8FAC" }}
                >
                  <input
                    id="contact-consent"
                    data-testid="contact-consent"
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => update("consent", e.target.checked)}
                    className="mt-[3px] h-4 w-4 shrink-0 appearance-none border cursor-pointer transition-colors duration-200"
                    style={{ borderColor: "#2A2233", backgroundColor: form.consent ? "#C724B1" : "#0E0A16" }}
                  />
                  <span className="text-xs leading-relaxed">
                    I agree to VYNLWORLD storing my details to reply to this enquiry, as described in the{" "}
                    <a
                      href="/privacy"
                      data-testid="contact-privacy-link"
                      className="underline transition-colors duration-300 hover:text-[#C724B1]"
                      style={{ color: "#F4EEF7" }}
                    >
                      Privacy Notice
                    </a>
                    .
                  </span>
                </label>

                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="btn-sharp w-full text-xs uppercase tracking-[0.22em] px-6 py-4 font-medium disabled:opacity-60"
                  style={{ backgroundColor: "#C724B1", color: "#F4EEF7" }}
                >
                  {submitting ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
